import {
  Conversation,
  File,
  Id,
  MediaFile,
  Member,
  MemberAlias,
  MessageAction,
  User,
} from '@nextforce/api'
import { Avatar, Icon, ListItem, Switch, Text, makeStyles } from '@rneui/themed'
import { t } from 'app/i18l'
import { font } from 'app/theme'
import { MemberAliasSlugList } from 'chat/members/views/alias_slug_list'
import { MemberCard } from 'chat/members/views/card'
import { ChatGroupDocumentGridItem } from 'file_folder_repository/views/grid_item'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ActivityIndicator,
  SectionList,
  SectionListData,
  View,
} from 'react-native'
import { FlatList } from 'react-native-gesture-handler'
import { Menu } from 'react-native-popup-menu'
import { SafeAreaView } from 'react-native-safe-area-context'
import { confirmationAlert } from 'shared/alerts'
import ActionSheetMenu from 'shared/components/chat/action_sheet_menu'
import { PressableOpacity } from 'shared/components/pressable_opacity'
import { RoundedButton } from 'shared/components/rounded-button'
import { pickImageFromLibrary } from 'shared/image_picker'
import { isRepository } from '../utils'
import { ConversationIcon } from '../views/card'

interface Props {
  conversation: Conversation | undefined
  nonAliasMembers: readonly Member[] | null | undefined
  memberAliases: MemberAlias[] | undefined
  user: User | undefined
  recentFiles: File[] | undefined
  recentFilesLoading: boolean
  totalMemberCount?: number | undefined
  onAddMembers(): void
  onRemoveMember(member: Member): void
  onSendDirectMessage(recipientId: Id): void
  onToggleAdminStatus(member: Member): void
  onDelete(): void
  onLeave(): void
  onEditName(): void
  onEditDescription(): void
  onUploadIcon(icon: MediaFile): Promise<void>
  onMute(muted: boolean): void
  onFavorite(favorite: boolean): void
  onToggleManagerReadOnly(managerReadOnly: boolean): void
  onToggleCrewReadOnly(crewReadOnly: boolean): void
  onToggleBirthdayWorkAnniversaryAndNewHireReminders(
    birthdayWorkAnniversaryAndNewHireReminders: boolean
  ): void
  onSeeAllFiles(): void
  onRecentDocumentPress(file: File): void
  onRecentDocumentLongPress(file: File): void
  isMembersLoading?: boolean
  onPressViewAllMembers: (search?: boolean) => void
  onAliasRemoved: (alias: MemberAlias) => void
}

export default function ConversationDetailsView({
  conversation,
  nonAliasMembers,
  memberAliases,
  user,
  recentFiles,
  recentFilesLoading,
  totalMemberCount,
  onSeeAllFiles,
  isMembersLoading,
  onAddMembers,
  onRemoveMember,
  onSendDirectMessage,
  onToggleAdminStatus,
  onDelete,
  onLeave,
  onEditName,
  onEditDescription,
  onUploadIcon,
  onMute,
  onFavorite,
  onToggleCrewReadOnly,
  onToggleManagerReadOnly,
  onToggleBirthdayWorkAnniversaryAndNewHireReminders,
  onRecentDocumentPress,
  onRecentDocumentLongPress,
  onPressViewAllMembers,
  onAliasRemoved,
}: Props) {
  const styles = useStyles()
  const [favorite, setFavorite] = useState(false)
  const [muted, setMuted] = useState(false)
  const [managerReadOnly, setManagerReadOnly] = useState<boolean | undefined>(
    false
  )
  const [crewReadOnly, setCrewReadOnly] = useState<boolean | undefined>(false)
  const [
    birthdayWorkAnniversaryAndNewHire,
    setBirthdayWorkAnniversaryAndNewHire,
  ] = useState<boolean | undefined>(
    conversation?.birthdayWorkAnniversaryAndNewHireRemindersEnabled
  )
  const [uploadingIcon, setUploadingIcon] = useState(false)
  const [selectedMember, setSelectedMember] = useState<Member | null>(null)
  const menuRef = useRef<Menu | null>(null)

  const showLeaveGroupButton =
    conversation?.type === 'custom' ||
    conversation?.type === 'adhoc' ||
    conversation?.type === 'repository'
  const showDeleteGroupButton = conversation?.allowEdit
  const showRecentFiles = conversation?.showRecentFiles
  const canEditNameAndIcon =
    conversation?.allowEdit && conversation.type !== 'adhoc'

  useEffect(() => {
    if (conversation) {
      setMuted(conversation.muted)
      setFavorite(conversation.favorite ?? false)
      setCrewReadOnly(conversation.crewReadOnly)
      setManagerReadOnly(conversation.managerReadOnly)
    }
  }, [conversation])

  interface MenuItem {
    name: string
    title: string
  }

  const getMenuItems = useCallback(
    (member: Member) => {
      const items: MenuItem[] = [
        ...(conversation?.allowEdit
          ? [{ name: 'removeMember', title: t('removeMember') }]
          : []),
        ...(conversation?.messageActions.some(
          action => action.name === 'sendDirectMessage'
        )
          ? [{ name: 'sendDirectMessage', title: t('sendDirectMessage') }]
          : []),
        ...(conversation?.messageActions.some(
          action => action.name === 'assignAdmin'
        )
          ? [
              {
                name: 'toggleGroupAdmin',
                title: member.isAdmin
                  ? t('removeGroupAdmin')
                  : t('makeGroupAdmin'),
              },
            ]
          : []),
        { name: 'cancel', title: t('cancel') },
      ]
      return items
    },
    [conversation?.allowEdit, conversation?.messageActions]
  )

  interface BaseItem {
    key: string
    info?: string
  }

  interface IconItem extends BaseItem {
    type: 'icon'
  }

  interface NameItem extends BaseItem {
    type: 'name'
  }

  interface DescriptionItem extends BaseItem {
    type: 'description'
  }

  interface MuteItem extends BaseItem {
    type: 'mute'
  }

  interface FavoriteItem extends BaseItem {
    type: 'favorite'
  }

  interface RepositoryItem extends BaseItem {
    type: 'repository'
  }

  interface AddMembersItem extends BaseItem {
    type: 'addMembers'
  }

  interface MemberItem extends BaseItem {
    type: 'member'
    member: Member
  }

  interface MemberAliasItem extends BaseItem {
    type: 'memberAlias'
  }

  interface ManagerReadOnlyItem extends BaseItem {
    type: 'managerReadOnly'
  }

  interface CrewReadOnlyItem extends BaseItem {
    type: 'crewReadOnly'
  }

  interface BirthdayWorkAnniversaryAndNewHireRemindersItem extends BaseItem {
    type: 'birthdayWorkAnniversaryAndNewHireReminders'
  }

  interface RecentFilesItem extends BaseItem {
    type: 'recentFiles'
  }

  interface Button extends BaseItem {
    type: 'button'
    title: string
    buttonType: 'danger' | 'primary'
    onPress: () => void
  }

  interface ViewAllButton extends BaseItem {
    type: 'viewAllButton'
    title: string
    onPress: () => void
  }

  type Item =
    | IconItem
    | NameItem
    | DescriptionItem
    | MuteItem
    | FavoriteItem
    | AddMembersItem
    | MemberItem
    | ManagerReadOnlyItem
    | CrewReadOnlyItem
    | BirthdayWorkAnniversaryAndNewHireRemindersItem
    | RecentFilesItem
    | RepositoryItem
    | Button
    | ViewAllButton
    | MemberAliasItem

  type Section = SectionListData<
    Item,
    {
      name: string
      title?: string
      icon?: { name: string; type: string }
      onPressIcon?: () => void
      count?: number
      loading?: boolean
    }
  >

  const onDeleteButtonPress = useCallback(async () => {
    const confirmed = await confirmationAlert(t('deleteGroupConfirmation'))

    if (confirmed) {
      onDelete()
    }
  }, [onDelete])

  const onLeaveButtonPress = useCallback(async () => {
    const isAdmin = conversation?.allowEdit
    const confirmed = isAdmin
      ? await confirmationAlert(
          t('leaveGroupConfirmation'),
          t('leaveGroupConfirmationAdminDescription')
        )
      : await confirmationAlert(
          t('leaveGroupConfirmation'),
          t('leaveGroupConfirmationDescription')
        )

    if (confirmed === true) {
      onLeave()
    }
  }, [conversation?.allowEdit, onLeave])

  const sections = useMemo(() => {
    const _sections: Section[] = [
      {
        name: 'icon',
        data: [{ type: 'icon', key: 'icon' }],
      },
      {
        name: 'info',
        data: [
          {
            type: 'name',
            key: 'name',
          },
          {
            type: 'description',
            key: 'description',
          },
          {
            type: 'mute',
            key: 'mute',
          },
          {
            type: 'favorite',
            key: 'favorite',
          },
        ],
      },
    ]

    if (!isRepository(conversation) && recentFiles?.length && showRecentFiles) {
      const recentFilesSection: Section = {
        name: 'recentFiles',
        data: [
          {
            type: 'recentFiles',
            key: 'recentFiles',
          },
        ],
      }
      _sections.push(recentFilesSection)
    }
    const readOnlySection: Section = {
      name: 'readOnly',
      title: t('readOnly'),
      data: [
        {
          type: 'managerReadOnly',
          key: 'managerReadOnly',
        },
        {
          type: 'crewReadOnly',
          key: 'crewReadOnly',
        },
      ],
    }

    const remindersSection: Section = {
      name: 'reminders',
      title: t('reminders'),
      data: [
        {
          type: 'birthdayWorkAnniversaryAndNewHireReminders',
          key: 'birthdayWorkAnniversaryAndNewHireReminders',
        },
      ],
    }

    if (conversation?.showReadOnly) {
      _sections.push(readOnlySection)
    }

    if (conversation?.showBirthdayWorkAnniversaryAndNewHireReminders) {
      _sections.push(remindersSection)
    }

    if (nonAliasMembers !== null) {
      const memberItems =
        nonAliasMembers?.slice(0, 10)?.map(
          member =>
            ({
              type: 'member',
              member,
              key: member.id,
            } as Item)
        ) ?? []

      const memberAliasItem = memberAliases?.length
        ? ([
            {
              type: 'memberAlias',
              key: 'memberAlias',
            },
          ] as Item[])
        : []

      const addMembersItems = conversation?.allowEdit
        ? [
            {
              type: 'addMembers',
              key: 'addMembers',
            } as Item,
          ]
        : []

      const viewAllMembersItems =
        nonAliasMembers && nonAliasMembers?.length > 1
          ? ([
              {
                type: 'viewAllButton',
                onPress: onPressViewAllMembers,
                key: 'viewAllButton',
              },
            ] as Item[])
          : []
      _sections.push({
        name: 'members',
        title: t('members'),
        count: totalMemberCount,
        loading: isMembersLoading,
        icon: { name: 'search', type: 'ant-design' },
        onPressIcon: () => onPressViewAllMembers(true),
        data: [
          ...memberAliasItem,
          ...addMembersItems,
          ...memberItems,
          ...viewAllMembersItems,
        ],
      })

      const buttonItems: Button[] = []

      if (showLeaveGroupButton) {
        buttonItems.push({
          type: 'button',
          key: 'leaveGroup',
          title: t('leaveGroup'),
          buttonType: 'danger',
          onPress: onLeaveButtonPress,
        })
      }
      if (showDeleteGroupButton) {
        buttonItems.push({
          type: 'button',
          key: 'deleteGroup',
          title: t('deleteGroup'),
          buttonType: 'danger',
          onPress: onDeleteButtonPress,
        })
      }
      _sections.push({
        name: 'buttons',
        data: buttonItems,
      })
    }

    return _sections
  }, [
    conversation,
    memberAliases?.length,
    nonAliasMembers,
    onDeleteButtonPress,
    onLeaveButtonPress,
    onPressViewAllMembers,
    recentFiles?.length,
    showDeleteGroupButton,
    showLeaveGroupButton,
    showRecentFiles,
  ])

  const memberActions = useMemo((): MessageAction[] => {
    if (!selectedMember) {
      return []
    }
    // Convert our custom member actions into the format ActionSheetMenu expects
    return getMenuItems(selectedMember).map(item => ({
      name: item.name as Exclude<MessageAction['name'], 'reaction'>,
      title: item.title,
    }))
  }, [selectedMember, getMenuItems])

  const handleMemberAction = useCallback(
    (action: MessageAction) => {
      if (!selectedMember) return

      const actionName = action.name as
        | 'removeMember'
        | 'sendDirectMessage'
        | 'toggleGroupAdmin'

      switch (actionName) {
        case 'removeMember':
          confirmationAlert(t('removeMemberConfirm'))
            .then(value => {
              if (value) {
                onRemoveMember(selectedMember)
              }
            })
            .catch(e => null)
          break
        case 'sendDirectMessage':
          onSendDirectMessage(selectedMember.id)
          break
        case 'toggleGroupAdmin':
          onToggleAdminStatus(selectedMember)
          break
        default:
          break
      }
    },
    [selectedMember, onRemoveMember, onSendDirectMessage, onToggleAdminStatus]
  )

  const onMemberPress = useCallback(
    (member: Member) => {
      const menuItems = getMenuItems(member)
      // Do not show menu for self or if no actions are available
      if (menuItems.length === 0 || member.id === user?.id) {
        return
      }
      setSelectedMember(member)
    },
    [getMenuItems, user?.id]
  )

  // Open menu when a member is selected
  useEffect(() => {
    if (selectedMember) {
      menuRef.current?.open().catch(e => null)
    }
  }, [selectedMember])

  const onMuteChange = useCallback(
    (value: boolean) => {
      setMuted(value)
      onMute(value)
    },
    [onMute]
  )

  const onManagerReadOnlyChange = useCallback(
    (value: boolean) => {
      setManagerReadOnly(value)
      onToggleManagerReadOnly(value)
    },
    [onToggleManagerReadOnly]
  )

  const onFavoriteChange = useCallback(
    (value: boolean) => {
      setFavorite(value)
      onFavorite(value)
    },
    [onFavorite]
  )

  const onCrewReadOnlyChange = useCallback(
    (value: boolean) => {
      setCrewReadOnly(value)
      onToggleCrewReadOnly(value)
    },
    [onToggleCrewReadOnly]
  )

  const onBirthdayWorkAnniversaryAndNewHireRemindersChange = useCallback(
    (value: boolean) => {
      setBirthdayWorkAnniversaryAndNewHire(value)
      onToggleBirthdayWorkAnniversaryAndNewHireReminders(value)
    },
    [onToggleBirthdayWorkAnniversaryAndNewHireReminders]
  )

  const renderItem = useCallback(
    ({ item }: { item: Item }) => {
      if (!conversation) {
        return null
      }

      switch (item?.type) {
        case 'icon':
          if (isRepository(conversation)) {
            return (
              <View style={styles.avatar}>
                <ConversationIcon conversation={conversation} size="large" />
              </View>
            )
          }
          return (
            <ConversationIcon
              conversation={conversation}
              containerStyle={styles.avatar}
              size={'large'}
            >
              {canEditNameAndIcon && !uploadingIcon && (
                <Avatar.Accessory
                  onPress={async () => {
                    const imageResult = await pickImageFromLibrary()

                    if (imageResult.status === 'success') {
                      setUploadingIcon(true)

                      try {
                        await onUploadIcon(imageResult.files[0])
                      } finally {
                        setUploadingIcon(false)
                      }
                    }
                  }}
                  size={20}
                />
              )}
            </ConversationIcon>
          )
        case 'name':
          return (
            <ListItem
              onPress={canEditNameAndIcon ? onEditName : null}
              disabled={!conversation.allowEdit}
              bottomDivider
            >
              <ListItem.Content>
                <ListItem.Title style={styles.name}>
                  {conversation.name}
                </ListItem.Title>
              </ListItem.Content>
              {canEditNameAndIcon ? <ListItem.Chevron /> : null}
            </ListItem>
          )
        case 'description':
          return (
            <ListItem
              onPress={onEditDescription}
              disabled={!conversation.allowEdit}
            >
              <ListItem.Content>
                <ListItem.Title style={styles.description}>
                  {conversation.description?.length
                    ? conversation.description
                    : t('descriptionPlaceholder')}
                </ListItem.Title>
              </ListItem.Content>
              {conversation.allowEdit ? <ListItem.Chevron /> : null}
            </ListItem>
          )
        case 'mute':
          return (
            <ListItem>
              <ListItem.Content>
                <ListItem.Title>Mute</ListItem.Title>
              </ListItem.Content>
              <Switch value={muted} onValueChange={onMuteChange} />
            </ListItem>
          )
        case 'favorite':
          return (
            <ListItem>
              <ListItem.Content>
                <ListItem.Title>Mark as Favorite ★</ListItem.Title>
              </ListItem.Content>
              <Switch value={favorite} onValueChange={onFavoriteChange} />
            </ListItem>
          )
        case 'recentFiles':
          if (recentFilesLoading) {
            return (
              <View>
                <ActivityIndicator color={styles.activityIndicator.color} />
              </View>
            )
          }
          return (
            <View>
              <ListItem
                onPress={onSeeAllFiles}
                containerStyle={styles.sectionHeaderContainer}
              >
                <ListItem.Content>
                  <ListItem.Title style={styles.sectionHeader}>
                    {t('recentFiles').toUpperCase()}
                  </ListItem.Title>
                </ListItem.Content>
                <ListItem.Chevron />
              </ListItem>
              <FlatList
                horizontal
                data={recentFiles}
                renderItem={({ item: mediaItem }) => {
                  return (
                    <ChatGroupDocumentGridItem
                      item={mediaItem}
                      onPress={onRecentDocumentPress}
                      onLongPress={onRecentDocumentLongPress}
                    />
                  )
                }}
                contentContainerStyle={styles.recentFilesListContainer}
                ListFooterComponent={() => (
                  <PressableOpacity onPress={onSeeAllFiles}>
                    <View style={styles.viewAllContainer}>
                      <Icon name={'arrowright'} type="ant-design" size={30} />
                      <Text style={[styles.subtitle, styles.viewAllText]}>
                        {t('viewAll')}
                      </Text>
                    </View>
                  </PressableOpacity>
                )}
                keyExtractor={mediaItem => mediaItem.id.toString()}
                ListEmptyComponent={() => (
                  <View style={styles.listEmptyComponentContainer}>
                    <Text style={styles.subtitle}>{t('noFiles')}</Text>
                  </View>
                )}
              />
            </View>
          )
        case 'managerReadOnly':
          return (
            <ListItem>
              <ListItem.Content>
                <ListItem.Title>{t('manager')}</ListItem.Title>
              </ListItem.Content>
              <Switch
                value={managerReadOnly}
                onValueChange={onManagerReadOnlyChange}
              />
            </ListItem>
          )
        case 'crewReadOnly':
          return (
            <ListItem>
              <ListItem.Content>
                <ListItem.Title>{t('crew')}</ListItem.Title>
              </ListItem.Content>
              <Switch
                value={crewReadOnly}
                onValueChange={onCrewReadOnlyChange}
              />
            </ListItem>
          )
        case 'birthdayWorkAnniversaryAndNewHireReminders':
          return (
            <ListItem>
              <ListItem.Content>
                <ListItem.Title>
                  {t('birthdayWorkAnniversaryAndNewHire')}
                </ListItem.Title>
              </ListItem.Content>
              <Switch
                value={birthdayWorkAnniversaryAndNewHire}
                onValueChange={
                  onBirthdayWorkAnniversaryAndNewHireRemindersChange
                }
              />
            </ListItem>
          )
        case 'addMembers':
          return (
            <View>
              <ListItem onPress={onAddMembers} bottomDivider>
                <Avatar
                  rounded
                  size="medium"
                  icon={{
                    name: 'plus',
                    color: styles.addMember.color,
                    type: 'ant-design',
                  }}
                  containerStyle={{
                    backgroundColor: styles.addMember.backgroundColor,
                  }}
                />
                <ListItem.Content>
                  <ListItem.Title style={styles.addMemberText}>
                    {t('addMembers')}
                  </ListItem.Title>
                </ListItem.Content>
              </ListItem>
            </View>
          )
        case 'member':
          return (
            <MemberCard
              member={item.member}
              onPress={onMemberPress}
              showAdminStatus
            />
          )
        case 'memberAlias':
          return (
            <View style={{ paddingHorizontal: 16 }}>
              <MemberAliasSlugList
                memberAliases={memberAliases}
                onAliasRemoved={
                  conversation.allowEdit ? onAliasRemoved : undefined
                }
              />
            </View>
          )
        case 'viewAllButton':
          return (
            <PressableOpacity onPress={() => item.onPress()}>
              <View style={styles.viewAllButtonContainer}>
                <Text style={styles.viewAllButtonText}>{t('viewAll')}</Text>
                <Icon
                  name="caret-down"
                  type="ant-design"
                  size={15}
                  color={styles.viewAllButtonIcon.color}
                  style={styles.viewAllButtonIcon}
                />
              </View>
            </PressableOpacity>
          )
        case 'button':
          return (
            <View key={item.key} style={styles.buttonBox}>
              <RoundedButton
                title={item.title}
                onPress={item.onPress}
                danger={item.buttonType === 'danger'}
              />
            </View>
          )
        default:
          return null
      }
    },
    [
      conversation,
      styles.avatar,
      styles.name,
      styles.description,
      styles.sectionHeaderContainer,
      styles.sectionHeader,
      styles.recentFilesListContainer,
      styles.addMember.color,
      styles.addMember.backgroundColor,
      styles.addMemberText,
      styles.viewAllButtonContainer,
      styles.viewAllButtonText,
      styles.viewAllButtonIcon,
      styles.buttonBox,
      styles.activityIndicator.color,
      styles.viewAllContainer,
      styles.subtitle,
      styles.viewAllText,
      styles.listEmptyComponentContainer,
      canEditNameAndIcon,
      uploadingIcon,
      onEditName,
      onEditDescription,
      muted,
      onMuteChange,
      favorite,
      onFavoriteChange,
      recentFilesLoading,
      onSeeAllFiles,
      recentFiles,
      managerReadOnly,
      onManagerReadOnlyChange,
      crewReadOnly,
      onCrewReadOnlyChange,
      birthdayWorkAnniversaryAndNewHire,
      onBirthdayWorkAnniversaryAndNewHireRemindersChange,
      onAddMembers,
      onMemberPress,
      memberAliases,
      onAliasRemoved,
      onUploadIcon,
      onRecentDocumentPress,
      onRecentDocumentLongPress,
    ]
  )

  if (!conversation) {
    return null
  }

  return (
    <SafeAreaView edges={['left', 'right', 'bottom']} style={styles.root}>
      <View style={styles.container}>
        <SectionList
          sections={sections}
          renderItem={renderItem}
          contentContainerStyle={styles.sectionListContainer}
          renderSectionHeader={({ section }) =>
            section.title ? (
              <View>
                <View style={styles.sectionHeaderContainer}>
                  <View style={styles.sectionHeaderTitleContainer}>
                    <Text style={styles.sectionHeader}>
                      {section.title.toUpperCase()}
                    </Text>
                    {section.loading ? (
                      <View
                        style={[styles.sectionHeader, styles.countContainer]}
                      >
                        <ActivityIndicator color={'white'} />
                      </View>
                    ) : null}
                    {!section.loading && section.count ? (
                      <View
                        style={[styles.sectionHeader, styles.countContainer]}
                      >
                        <Text style={[styles.count]}>{section.count}</Text>
                      </View>
                    ) : null}
                  </View>
                  {section.icon ? (
                    <PressableOpacity
                      onPress={section.onPressIcon}
                      style={styles.sectionIcon}
                    >
                      <Icon name={section.icon.name} size={30} />
                    </PressableOpacity>
                  ) : null}
                </View>
              </View>
            ) : null
          }
        />
        <ActionSheetMenu
          menuRef={menuRef}
          actions={memberActions}
          onAction={handleMemberAction}
          onClose={() => setSelectedMember(null)}
        />
      </View>
    </SafeAreaView>
  )
}

const useStyles = makeStyles(theme => ({
  root: {
    flex: 1,
  },
  container: {
    flex: 1,
  },
  avatar: {
    alignSelf: 'center',
    margin: 20,
  },
  name: {
    fontSize: 19,
    fontFamily: font.bold,
    lineHeight: 21,
  },
  description: {
    fontSize: 15,
    color: theme.colors?.grey3,
  },
  addMember: {
    color: theme.colors?.black,
    backgroundColor: theme.colors?.grey5,
  },
  addMemberText: {
    color: theme.colors?.primary,
  },
  sectionHeader: {
    fontSize: 13,
    color: theme.colors?.grey1,
    marginTop: 20,
    marginBottom: 10,
    marginLeft: 12,
  },
  buttonBox: {
    justifyContent: 'center',
    marginTop: 15,
    marginHorizontal: 20,
  },
  activityIndicator: {
    color: theme.colors.primary,
  },
  recentFilesListContainer: {
    paddingBottom: 5,
    alignItems: 'center',
    flexGrow: 1,
    paddingLeft: 10,
  },
  viewAllContainer: {
    paddingHorizontal: 10,
    margin: 5,
  },
  listEmptyComponentContainer: { flex: 1, marginLeft: 4 },
  sectionHeaderContainer: {
    backgroundColor: theme.colors.white,
    paddingVertical: 0,
    paddingLeft: 0,
    flexDirection: 'row',
    alignItems: 'center',
  },
  viewAllText: { marginTop: 10, fontSize: 14 },
  subtitle: {
    fontSize: 17,
  },
  count: {
    textAlignVertical: 'center',

    fontSize: 14,
    color: 'white',
  },
  countContainer: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    backgroundColor: theme.colors.primary,
    borderRadius: 15,
  },
  sectionIcon: {
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 20,
    marginTop: 20,
    marginBottom: 10,
  },
  sectionHeaderTitleContainer: {
    flexDirection: 'row',
    flex: 1,
    alignItems: 'center',
  },
  sectionListContainer: {
    paddingBottom: 30,
  },
  viewAllButtonContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    height: 70,
    flexDirection: 'row',
  },
  viewAllButtonText: { fontSize: 16, color: theme.colors.primary },
  viewAllButtonIcon: { marginLeft: 5, color: theme.colors.primary },
}))
